import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Save, FileDown } from 'lucide-react';
import authedFetch from '../../lib/authedFetch';
import OnboardingBaselineStep from './OnboardingBaselineStep';

const API = process.env.REACT_APP_BACKEND_URL;

const DOMAINS = [
  { key: 'lms', label: 'LMS' },
  { key: 'hris', label: 'HRIS' },
  { key: 'surveys', label: 'Survey platform' },
  { key: 'performance', label: 'Performance / KPI system' },
];

const CADENCES = ['daily', 'weekly', 'monthly', 'quarterly', 'on-demand'];

export default function StepDataSources({ orgId, state, onSave, saving, shareToken }) {
  const [draft, setDraft] = useState(state.answers || {});
  useEffect(() => { setDraft(state.answers || {}); }, [state]);

  const setRow = (domainKey, patch) => {
    setDraft((d) => ({
      ...d,
      [domainKey]: { ...(d[domainKey] || {}), ...patch },
    }));
  };

  const downloadTemplate = async () => {
    const url = `${API}/api/onboarding/template/data_sources${orgId ? `?org_id=${orgId}` : ''}`;
    const r = shareToken ? await fetch(url) : await authedFetch(url);
    const csv = await r.text();
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'seertech_data_sources_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4" data-testid="onboarding-step-data-sources">
      {!shareToken && (
        <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-4" data-testid="onb-data-baseline">
          <OnboardingBaselineStep orgId={orgId} />
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Map where each data domain comes from so we can keep your numbers fresh.</p>
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="onb-data-template-btn">
          <FileDown className="h-3.5 w-3.5 mr-2" /> CSV template
        </Button>
      </div>
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-2 text-left">Domain</th>
              <th className="px-2 py-2 text-left">System name</th>
              <th className="px-2 py-2 text-left">Owner email</th>
              <th className="px-2 py-2 text-left">Refresh cadence</th>
            </tr>
          </thead>
          <tbody>
            {DOMAINS.map((d) => {
              const row = draft[d.key] || {};
              return (
                <tr key={d.key} className="border-t border-slate-100" data-testid={`onb-data-row-${d.key}`}>
                  <td className="px-2 py-2 font-medium">{d.label}</td>
                  <td className="px-2 py-2">
                    <Input
                      value={row.system_name || ''}
                      onChange={(e) => setRow(d.key, { system_name: e.target.value })}
                      placeholder="e.g. Workday, Cornerstone…"
                      className="h-7 text-xs"
                      data-testid={`onb-data-system-${d.key}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="email"
                      value={row.owner_email || ''}
                      onChange={(e) => setRow(d.key, { owner_email: e.target.value })}
                      placeholder="owner@example.com"
                      className="h-7 text-xs"
                      data-testid={`onb-data-owner-${d.key}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={row.cadence || ''}
                      onChange={(e) => setRow(d.key, { cadence: e.target.value })}
                      className="h-7 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
                      data-testid={`onb-data-cadence-${d.key}`}
                    >
                      <option value="">—</option>
                      {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(draft)} disabled={saving} data-testid="onb-data-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-2" />}
          Save & continue
        </Button>
      </div>
    </div>
  );
}
